// Менюборд для телевизора (п.15 плана 29-ispravleniya-2026-09).
//
// Публичная страница /board/:id без авторизации и без Layout гостевого меню.
// Открывается в браузере Smart-TV/приставки в полноэкранном режиме; данные
// берёт из GET /api/client/establishments/:id/board (кеш 60 с на сервере),
// перезапрашивает каждую минуту — стоп-лист и цены на экране не отстают.
//
// Категории листаются по кругу с интервалом board.rotate_sec; на одном экране
// показывается одна категория, чтобы шрифт оставался читаемым с расстояния.
// Если категорий больше одной — снизу «точки» прогресса, как у карусели.
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { get } from '@/services/api';
import { formatRub } from '@/utils/format';
import { asAbsoluteUrl } from '@/utils/url';

type BoardResponse = {
  establishment: {
    id: string;
    name: string;
    logo_url: string | null;
    theme: { primary?: string | null; fontFamily?: string | null } | null;
  };
  board: {
    enabled: boolean;
    categoryIds?: string[];
    rotateSec?: number | null;
    showPhotos?: boolean;
    showPrices?: boolean;
    title?: string | null;
  };
  categories: Array<{
    id: string;
    name: string;
    discount_percent?: number;
    dishes: Array<{
      id: string;
      name: string;
      price: number;
      base_price?: number;
      description: string;
      photoUrl: string | null;
      weight: string | null;
    }>;
  }>;
};

const REFRESH_MS = 60_000;

export default function Board() {
  const { id = '' } = useParams<{ id: string }>();

  const { data, error, isLoading } = useQuery({
    queryKey: ['board', id],
    queryFn: () => get<BoardResponse>(`/api/client/establishments/${id}/board`),
    enabled: Boolean(id),
    refetchInterval: REFRESH_MS,
    staleTime: REFRESH_MS,
    retry: 2,
  });

  // Пустые категории (всё в стоп-листе) не показываем — на ТВ они выглядят как сбой.
  const categories = useMemo(
    () => (data?.categories ?? []).filter((c) => c.dishes.length > 0),
    [data?.categories],
  );

  const rotateMs = Math.max(5, data?.board.rotateSec ?? 12) * 1000;
  const [index, setIndex] = useState(0);

  // Ротация категорий. Сбрасываем индекс, если список стал короче.
  useEffect(() => {
    if (categories.length <= 1) {
      setIndex(0);
      return;
    }
    const t = window.setInterval(() => {
      setIndex((i) => (i + 1) % categories.length);
    }, rotateMs);
    return () => window.clearInterval(t);
  }, [categories.length, rotateMs]);

  useEffect(() => {
    if (index >= categories.length) setIndex(0);
  }, [categories.length, index]);

  // Тема заведения: акцентный цвет и шрифт из настроек «Меню и бренд».
  const primary = data?.establishment.theme?.primary || '#4f62e0';
  const fontFamily = data?.establishment.theme?.fontFamily || undefined;

  useEffect(() => {
    document.title = data ? `${data.establishment.name} — меню` : 'Меню';
  }, [data]);

  if (isLoading) {
    return <Screen primary={primary}><Center>Загружаем меню…</Center></Screen>;
  }
  if (error || !data) {
    return (
      <Screen primary={primary}>
        <Center>
          <div className="text-4xl font-semibold">Меню недоступно</div>
          <div className="mt-3 text-2xl opacity-70">Проверьте ссылку или подключение к интернету</div>
        </Center>
      </Screen>
    );
  }
  if (!data.board.enabled) {
    return (
      <Screen primary={primary}>
        <Center>
          <div className="text-4xl font-semibold">Менюборд выключен</div>
          <div className="mt-3 text-2xl opacity-70">Включите его в админке: Настройки → Касса → Меню на телевизор</div>
        </Center>
      </Screen>
    );
  }
  if (categories.length === 0) {
    return (
      <Screen primary={primary}>
        <Center>
          <div className="text-4xl font-semibold">{data.board.title || data.establishment.name}</div>
          <div className="mt-3 text-2xl opacity-70">Скоро здесь появится меню</div>
        </Center>
      </Screen>
    );
  }

  const cat = categories[Math.min(index, categories.length - 1)];
  const showPhotos = data.board.showPhotos ?? true;
  const showPrices = data.board.showPrices ?? true;
  const logo = asAbsoluteUrl(data.establishment.logo_url);
  // Плотность сетки: мало блюд — крупнее карточки, много — компактнее.
  const dense = cat.dishes.length > 8;

  return (
    <Screen primary={primary} fontFamily={fontFamily}>
      <header className="flex items-center justify-between gap-6 px-12 pt-10 pb-6">
        <div className="flex items-center gap-5 min-w-0">
          {logo && (
            <img src={logo} alt="" className="h-16 w-16 rounded-2xl object-cover bg-white/10" />
          )}
          <div className="min-w-0">
            <div className="truncate text-2xl font-medium opacity-70">
              {data.board.title || data.establishment.name}
            </div>
            <h1 className="truncate text-5xl font-bold leading-tight">
              {cat.name}
              {cat.discount_percent ? (
                <span
                  className="ml-4 inline-block rounded-full px-4 py-1 align-middle text-2xl font-semibold text-white"
                  style={{ background: primary }}
                >
                  −{cat.discount_percent}%
                </span>
              ) : null}
            </h1>
          </div>
        </div>
        {categories.length > 1 && (
          <div className="flex shrink-0 items-center gap-2" aria-hidden>
            {categories.map((c, i) => (
              <span
                key={c.id}
                className="h-2.5 rounded-full transition-all duration-500"
                style={{
                  width: i === index ? 28 : 10,
                  background: i === index ? primary : 'rgba(255,255,255,0.25)',
                }}
              />
            ))}
          </div>
        )}
      </header>

      <main
        key={cat.id}
        className={`board-fade grid flex-1 content-start gap-5 px-12 pb-10 ${
          dense ? 'grid-cols-4' : 'grid-cols-3'
        }`}
      >
        {cat.dishes.map((d) => {
          const photo = showPhotos ? asAbsoluteUrl(d.photoUrl) : null;
          const discounted = d.base_price !== undefined && d.base_price > d.price;
          return (
            <article
              key={d.id}
              className="flex overflow-hidden rounded-3xl bg-white/[0.06] ring-1 ring-white/10"
            >
              {photo && (
                <img
                  src={photo}
                  alt=""
                  className={`${dense ? 'h-28 w-28' : 'h-40 w-40'} shrink-0 object-cover`}
                  loading="lazy"
                />
              )}
              <div className="flex min-w-0 flex-1 flex-col justify-between p-5">
                <div className="min-w-0">
                  <div className={`${dense ? 'text-2xl' : 'text-3xl'} font-semibold leading-snug line-clamp-2`}>
                    {d.name}
                  </div>
                  {d.weight && <div className="mt-1 text-xl opacity-60">{d.weight}</div>}
                  {!dense && d.description && (
                    <div className="mt-2 text-lg leading-snug opacity-60 line-clamp-2">{d.description}</div>
                  )}
                </div>
                {showPrices && (
                  <div className="mt-3 flex items-baseline gap-3">
                    <span className={`${dense ? 'text-2xl' : 'text-3xl'} font-bold`} style={{ color: primary }}>
                      {formatRub(d.price)}
                    </span>
                    {discounted && (
                      <span className="text-xl line-through opacity-50">{formatRub(d.base_price!)}</span>
                    )}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </main>
    </Screen>
  );
}

function Screen({ children, primary, fontFamily }: {
  children: React.ReactNode;
  primary: string;
  fontFamily?: string;
}) {
  return (
    <div
      className="flex min-h-screen flex-col bg-[#0f1117] text-white select-none"
      style={{ fontFamily, ['--board-primary' as string]: primary }}
    >
      <style>{`
        @keyframes board-fade { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        .board-fade { animation: board-fade .5s ease-out both; }
        @media (prefers-reduced-motion: reduce) { .board-fade { animation: none; } }
        html, body { background: #0f1117; overflow: hidden; }
        ::-webkit-scrollbar { display: none; }
      `}</style>
      {children}
    </div>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-12 text-center">
      {children}
    </div>
  );
}
